import React, { useEffect, useState } from 'react';
import ImageChooseModal from './modal';
import { NXDelete } from "../../icons";
import { AxiosResponse } from "axios";
import useApi from "../../api";
import { toast } from 'react-toastify';

interface ImageData {
    id: number;
    image: string;
}

interface ImageFieldProps {
    label?: string;
    value?: ImageData[] | ImageData;
    name: string;
    onChange: any;
    isMull?: boolean;
}

const ImageField: React.FC<ImageFieldProps> = ({ label = 'Media Gallery', value, name, onChange, isMull = false }) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const [valueList, setValueList] = useState<ImageData[]>([]);
    const [isImageDropped, setIsImageDropped] = useState(false);
    const api = useApi();

    const uploadImageHandle = (e: any) => {
        if (!e.target.files[0]) return;
        
        const data = new FormData();
        data.append("name", e.target.files[0].name);
        data.append("image", e.target.files[0]);
        data.append("image_alt_text", e.target.files[0].name);

        api.postImage(data)
            .then((response: AxiosResponse) => {
                const ImageResponse = response as unknown as any;
                const newImage = { id: ImageResponse.id, image: ImageResponse.image };
                updateImageList(newImage);
            })
            .catch(() => toast.error('Upload image failed!'));
    };

    const updateImageList = (newImage: ImageData | ImageData[]) => {
        setValueList((prevValue) => {
            const images = Array.isArray(newImage) ? newImage : [newImage];
            
            // Handle single image mode
            if (!isMull) {
                const newValue = [images[0]];
                onChange(name, newValue);
                return newValue;
            } 
            
            // Handle multi-image mode
            const newValue = [...prevValue, ...images];
            onChange(name, newValue);
            return newValue;
        });
    };

    const onSelectedSave = (data: ImageData[]) => {
        if (!data.length) return;
        
        if (!isMull) {
            updateImageList(data[0]);
        } else {
            updateImageList(data);
        }
    };

    const deleteImage = (e: React.MouseEvent, imgId: number) => {
        e.preventDefault();
        e.stopPropagation();
        setValueList((prevValue) => {
            const newValue = prevValue.filter((imgData) => imgData.id !== imgId);
            onChange(name, newValue);
            return newValue;
        });
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!isMull && valueList.length > 0) {
            setIsImageDropped(false);
            return;
        }
        
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            const data = new FormData();
            data.append("name", droppedFile.name);
            data.append("image", droppedFile);
            data.append("image_alt_text", droppedFile.name);

            api.postImage(data)
                .then((response: AxiosResponse) => {
                    const ImageResponse = response as unknown as any;
                    const newImage = { id: ImageResponse.id, image: ImageResponse.image };
                    updateImageList(newImage);
                })
                .catch(() => toast.error('Drag and drop image failed!'));
            
            setIsImageDropped(false);
        }
    };

    // Initialize component with existing images
    useEffect(() => {
        if (value) {
            setValueList(Array.isArray(value) ? value : [value]);
        }
    }, [value]);

    const shouldShowButtons = isMull || valueList.length === 0;

    return (
        <>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mt-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-medium text-gray-800">{label}</h3>
                </div>

                {valueList.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                        {valueList.map((imgData) => (
                            <div key={imgData.id} className="group relative rounded-lg overflow-hidden bg-gray-50">
                                <div className="aspect-square w-full overflow-hidden">
                                    <img
                                        src={imgData.image}
                                        alt=""
                                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                                    />
                                </div>
                                <div className="absolute top-1 right-1 z-10" onClick={(e) => e.stopPropagation()}>
                                    <a
                                        href="#"
                                        onClick={(e) => deleteImage(e, imgData.id)}
                                        className="flex items-center justify-center w-6 h-6 bg-white rounded-full shadow hover:bg-red-50 transition-colors"
                                    >
                                        <NXDelete className="h-4 w-4 text-red-600" />
                                    </a>
                                </div>
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200"></div>
                            </div>
                        ))}
                        
                        {isMull && (
                            <>
                                <div
                                    onClick={() => document.getElementById('media')?.click()}
                                    className="aspect-square flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
                                >
                                    <svg className="w-7 h-7 text-gray-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    <span className="text-xs text-gray-500">Add New</span>
                                </div>
                                
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(true)}
                                    className="inline-flex items-center px-2 py-1.5 text-xs font-medium rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100 transition"
                                >
                                    <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Select Existing
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    <div
                        className={`
                            rounded-lg border-2 border-dashed transition-all duration-200
                            ${isImageDropped ? 'border-[#0CAF60] bg-[#0CAF60]/10' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'}
                        `}
                        onDrop={handleDrop}
                        onDragOver={(e) => e.preventDefault()}
                        onDragEnter={() => shouldShowButtons && setIsImageDropped(true)}
                        onDragLeave={() => setIsImageDropped(false)}
                    >
                        <div className="px-4 py-6 text-center">
                            <div className="mx-auto h-12 w-12 text-gray-400 mb-2">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-full h-full">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="1.5"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <h4 className="text-sm font-medium text-gray-700 mb-1">Drop files to upload</h4>
                            <p className="text-xs text-gray-500 mb-3">
                                Accept images or 3D models
                            </p>
                            <div className="flex items-center justify-center gap-2">
                                <label
                                    htmlFor="media"
                                    className="px-3 py-1.5 rounded-md bg-[#0CAF60] text-white hover:bg-[#098a4e] transition font-medium cursor-pointer text-xs inline-flex items-center justify-center"
                                >
                                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    Browse Files
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(true)}
                                    className="px-3 py-1.5 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition font-medium text-xs inline-flex items-center justify-center"
                                >
                                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                    Select Media
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <input
                    id="media"
                    type="file"
                    className="hidden"
                    accept="image/*,video/*,.glb,.gltf"
                    onChange={uploadImageHandle}
                />
            </div>
            <ImageChooseModal isMull={isMull} onSelectedSave={onSelectedSave} isOpen={isModalOpen} onClose={() => setModalOpen(false)} />
        </>
    );
};

export default ImageField;